import React from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import type { StackScreenProps } from "@react-navigation/stack";
import { usePatient } from "@louise/patients/hooks/usePatient";
import {
  MenstrualHealthProvider,
  getResumeRoute,
} from "./MenstrualHealthContext";
import { CycleStatusScreen } from "./screens/CycleStatusScreen";
import { AgeAtFirstPeriodScreen } from "./screens/AgeAtFirstPeriodScreen";
import { CycleDetailsScreen } from "./screens/CycleDetailsScreen";
import { Last3PeriodsScreen } from "./screens/Last3PeriodsScreen";
import { BleedOutsidePeriodScreen } from "./screens/BleedOutsidePeriodScreen";
import { PainScaleScreen } from "./screens/PainScaleScreen";
import { PainMedicationScreen } from "./screens/PainMedicationScreen";
import { SymptomsScreen } from "./screens/SymptomsScreen";
import { FertileWindowScreen } from "./screens/FertileWindowScreen";
import { MenstrualHealthCompleteScreen } from "./screens/MenstrualHealthCompleteScreen";
import { MedicalHistoryQuestionProvider } from "@src/packages/medical-history/components";

export type MenstrualHealthStackParamList = {
  CycleStatus: undefined;
  AgeAtFirstPeriod: undefined;
  CycleDetails: undefined;
  Last3Periods: undefined;
  BleedOutsidePeriod: undefined;
  PainScale: undefined;
  PainMedication: undefined;
  Symptoms: undefined;
  FertileWindow: undefined;
  MenstrualHealthComplete: undefined;
};

export type MenstrualHealthStackScreenProps<
  T extends keyof MenstrualHealthStackParamList,
> = StackScreenProps<MenstrualHealthStackParamList, T>;

const Stack = createNativeStackNavigator<MenstrualHealthStackParamList>();

export function MenstrualHealthNavigator() {
  const { data: patient } = usePatient();
  const initialRoute = getResumeRoute(patient);

  return (
    <MenstrualHealthProvider>
      <MedicalHistoryQuestionProvider
        section="menstrual-health"
        sectionLabelKey="med_history.review.section_title.menstrual-health"
        helpTextKey="med_history.question_screen.flow_help.menstrual-health"
      >
        <Stack.Navigator
          initialRouteName={initialRoute}
          screenOptions={{
            headerShown: false,
            contentStyle: { backgroundColor: "transparent" },
          }}
        >
          <Stack.Screen name="CycleStatus" component={CycleStatusScreen} />
          <Stack.Screen
            name="AgeAtFirstPeriod"
            component={AgeAtFirstPeriodScreen}
          />
          <Stack.Screen name="CycleDetails" component={CycleDetailsScreen} />
          <Stack.Screen name="Last3Periods" component={Last3PeriodsScreen} />
          <Stack.Screen
            name="BleedOutsidePeriod"
            component={BleedOutsidePeriodScreen}
          />
          <Stack.Screen name="PainScale" component={PainScaleScreen} />
          <Stack.Screen
            name="PainMedication"
            component={PainMedicationScreen}
          />
          <Stack.Screen name="Symptoms" component={SymptomsScreen} />
          <Stack.Screen name="FertileWindow" component={FertileWindowScreen} />
          <Stack.Screen
            name="MenstrualHealthComplete"
            component={MenstrualHealthCompleteScreen}
          />
        </Stack.Navigator>
      </MedicalHistoryQuestionProvider>
    </MenstrualHealthProvider>
  );
}
